import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Copy } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/useToast"
import { BODY_COLLAPSE_THRESHOLD_CHARACTERS } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { formatBetterDocBody } from "@/modules/admin/better-doc/lib/format-better-doc-body"

type BetterDocBodyViewerProps = {
  title: string
  body: string | null
  testId: string
}

export const BetterDocBodyViewer = ({ title, body, testId }: BetterDocBodyViewerProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isExpanded, setIsExpanded] = useState(false)

  const bodyLength = body === null ? 0 : body.length
  const isCollapsed = bodyLength > BODY_COLLAPSE_THRESHOLD_CHARACTERS && !isExpanded
  const displayedBody = useMemo(
    () => (body === null || isCollapsed ? null : formatBetterDocBody(body)),
    [body, isCollapsed],
  )

  const handleCopy = () => {
    if (body === null) {
      return
    }

    navigator.clipboard.writeText(body)
    toast({ description: t("admin.betterDoc.copied") })
  }

  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-on-surface text-sm font-medium">{title}</h4>
        {body === null ? null : (
          <Button
            type="button"
            variant="tertiary"
            className="p-2"
            aria-label={t("admin.betterDoc.copy")}
            onClick={handleCopy}
            data-testid={`${testId}-copy-button`}>
            <Copy className="size-4" />
          </Button>
        )}
      </div>
      {body === null ? (
        <p className="text-on-surface-subdued text-sm" data-testid={`${testId}-no-body`}>
          {t("admin.betterDoc.bodyViewer.noBody")}
        </p>
      ) : null}
      {isCollapsed ? (
        <div className="bg-surface-subdued flex items-center justify-between gap-4 rounded-md p-4">
          <p className="text-on-surface-subdued text-sm" data-testid={`${testId}-collapsed`}>
            {t("admin.betterDoc.bodyViewer.collapsed", { size: bodyLength.toLocaleString() })}
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setIsExpanded(true)}
            data-testid={`${testId}-expand-button`}>
            {t("admin.betterDoc.bodyViewer.expand")}
          </Button>
        </div>
      ) : null}
      {displayedBody === null ? null : (
        <pre
          className="bg-surface-subdued text-on-surface max-h-150 overflow-auto rounded-md p-4 text-sm wrap-break-word whitespace-pre-wrap"
          data-testid={`${testId}-content`}>
          {displayedBody}
        </pre>
      )}
    </section>
  )
}
